import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiGrid, 
  FiHome, 
  FiUsers, 
  FiUser, 
  FiPlus,
  FiMenu,
  FiX,
  FiLogOut,
  FiSettings,
  FiPlay,

} from 'react-icons/fi';
import './Layout.css';

const Layout = () => {
  const { partner, logout } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuTrayRef = React.useRef(null);
  const headerOptionsRef = React.useRef(null);

  const navigation = [
    { path: '/dashboard', label: 'My Dashboard', icon: FiHome },
    { path: '/extensions', label: 'Extensions', icon: FiGrid },
    { path: '/partners', label: 'Partners', icon: FiUsers },
    { path: '/users', label: 'All End Users', icon: FiUser },
  ];

  const handleLogout = () => {
    logout();
  };

  // Fix menu positioning for resellers to prevent it from extending beyond viewport
  React.useEffect(() => {
    if (isMenuOpen && partner?.partnerType === 'reseller' && menuTrayRef.current && headerOptionsRef.current) {
      const menu = menuTrayRef.current;
      const container = headerOptionsRef.current;
      const containerRect = container.getBoundingClientRect();
      const menuWidth = menu.offsetWidth || 250; // min-width is 250px
      const viewportWidth = window.innerWidth;
      
      // Check if menu would extend beyond right edge of viewport
      const rightEdge = containerRect.right;
      const wouldExtend = (rightEdge + menuWidth) > viewportWidth;
      
      if (wouldExtend) {
        // Position menu so it doesn't extend beyond viewport
        const spaceOnRight = viewportWidth - rightEdge;
        const spaceOnLeft = containerRect.left;
        
        if (spaceOnLeft >= menuWidth) {
          // Enough space on left, position from left edge
          menu.style.right = 'auto';
          menu.style.left = '0';
          menu.style.transform = 'translateX(-100%)';
        } else {
          // Not enough space on either side, position to fit within viewport
          menu.style.right = '0';
          menu.style.left = 'auto';
          menu.style.transform = 'translateX(0)';
          menu.style.maxWidth = `${spaceOnRight - 10}px`;
        }
      } else {
        // Reset to default positioning
        menu.style.right = '0';
        menu.style.left = 'auto';
        menu.style.transform = 'translateX(0)';
        menu.style.maxWidth = '';
      }
    }
  }, [isMenuOpen, partner?.partnerType]);

  return (
    <div className="layout">
      {/* Header */}
      <header className="header">
        <div className="container">
          <div className="navbar">
            <div className="navbar-header">
              <Link to="/dashboard" className="logo">
                <svg xmlns="http://www.w3.org/2000/svg" width="98" height="29.485" viewBox="0 0 98 29.485">
                  <g transform="translate(-52 -39.515)">
                    <g transform="translate(-403 -140)">
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(455 182)"></rect>
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(455 197)"></rect>
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(476 179.515) rotate(45)"></rect>
                      <rect className="logo-square" width="12" height="12" rx="3" transform="translate(470 197)"></rect>
                    </g>
                  </g>
                </svg>
              </Link>
            </div>

            <div className="navbar-container">
              {(() => {
                console.log('Partner data in Layout:', partner);
                console.log('Partner role:', partner?.role);
                console.log('Partner partnerType:', partner?.partnerType);
                console.log('Should show Create Extension:', partner && (partner.role === 'superadmin' || partner.role === 'admin' || partner.partnerType === 'ceo'));
                
                return partner && (partner.role === 'superadmin' || partner.role === 'admin' || partner.partnerType === 'ceo') ? (
                  <Link to="/create-extension" className="btn btn-blue btn-curved createBtn">
                    <FiPlus />
                    <span>Create Extension</span>
                  </Link>
                ) : null;
              })()}

              <div className="headerMenu">
                <Link to="/training-videos" className="btn trainingLink">
                  <FiPlay />
                  Training Videos
                </Link>

                <Link to="/api" className="btn">API Settings</Link>
                {(() => {
                  // Hide "Add Partner" button for resellers
                  if (partner?.partnerType === 'reseller') {
                    return null;
                  }
                  return (
                    <Link to="/create-partner" className="btn btn-hollow btn-hollow-green btn-medium btn-curved">
                      Add Partner
                    </Link>
                  );
                })()}
                <Link to="/create-user" className="btn btn-hollow btn-hollow-green btn-medium btn-curved">
                  Add User
                </Link>
              </div>

              <div 
                ref={headerOptionsRef}
                className={`headerOptions ${partner?.partnerType === 'reseller' ? 'reseller-menu' : ''}`}
              >
                <button className="userMenu" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                  <div className="userAvatar">
                    {partner?.initials || 'P'}
                  </div>
                </button>

                {isMenuOpen && (
                  <div ref={menuTrayRef} className="nav-menuTray">
                    <div className="userSnap d-flex f-align-center">
                      <div className="userAvatar">
                        {partner?.initials || 'P'}
                      </div>
                      <span className="welcomePanel">hello {partner?.name}</span>
                      <button className="btn closeUserMenu" onClick={() => setIsMenuOpen(false)}>
                        <FiX />
                      </button>
                    </div>
                    <ul>
                      <li>
                        <Link to="/dashboard" onClick={() => setIsMenuOpen(false)}>
                          <FiGrid />
                          My Dashboard
                        </Link>
                      </li>
                      <li>
                        <Link to="/update-password" onClick={() => setIsMenuOpen(false)}>
                          <FiSettings />
                          Update Password
                        </Link>
                      </li>
                      <li>
                        <Link to="/training-videos" onClick={() => setIsMenuOpen(false)}>
                          <FiPlay />
                          Training Videos
                        </Link>
                      </li>
                      <li>
                        <button onClick={handleLogout} className="logoutSpan">
                          <FiLogOut />
                          Logout
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="main bgMain">
        <div className="bodyLoggedIn">
          <div className="mainInner">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="footer footerCommon">
        <div className="container">
          <p>Powered by <a target="_blank" rel="noopener noreferrer" href="https://facebook.com/nathanielbear">Nathaniel Bear</a> and <a target="_blank" rel="noopener noreferrer" href="https://stonefeathermarketing.com">SFM</a></p>
          <div className="socialFt">
            <a className="socFB" target="_blank" rel="noopener noreferrer" href="https://facebook.com/nathanielbear">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
                <path className="social-icon" d="M22,3.667A3.794,3.794,0,0,0,18.333,0H3.667A3.794,3.794,0,0,0,0,3.667V18.333A3.794,3.794,0,0,0,3.667,22H11V13.689H8.311V10.022H11V8.6c0-2.469,1.858-4.693,4.131-4.693h2.958V7.578H15.131a.9.9,0,0,0-.709.978v1.467h3.667v3.667H14.422V22h3.911A3.794,3.794,0,0,0,22,18.333Z"></path>
              </svg>
            </a>
            <a className="socMSG" target="_blank" rel="noopener noreferrer" href="https://www.messenger.com/t/nathanielbear">
              <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
                <path className="social-icon" d="M21.239,24H4.749A2.684,2.684,0,0,1,2,21.263V4.749A2.685,2.685,0,0,1,4.749,2h16.5A2.685,2.685,0,0,1,24,4.749v16.5A2.765,2.765,0,0,1,21.239,24ZM11.812,9.644,5.433,16.353l5.782-3.142,3.017,3.142,6.332-6.709-5.735,3.142Z" transform="translate(-1.998 -1.998)"></path>
              </svg>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout; 